import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/* Holds admin-authored content overrides fetched from the backend.
   Simulators read their cases through useCaseList(); FurtherReading reads
   through useReading(). When no override exists, the built-in default wins. */
const ContentContext = createContext({
  caseOverrides: {},
  readingOverrides: {},
  hiddenSims: {},
  freeSims: {},
  etaSims: {},
  contentReady: false,
  isSimVisible: () => true,
  isSimFree: () => false,
  getSimEta: () => null,
  refresh: () => {},
});

export const ContentProvider = ({ children }) => {
  const { user, authReady } = useAuth();
  const [caseOverrides, setCaseOverrides] = useState({});
  const [readingOverrides, setReadingOverrides] = useState({});
  const [hiddenSims, setHiddenSims] = useState({});
  const [freeSims, setFreeSims] = useState({});
  const [etaSims, setEtaSims] = useState({});
  const [contentReady, setContentReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(user ? "/api/content" : "/api/content/public");
      setCaseOverrides(data.cases || {});
      setReadingOverrides(data.reading || {});
      setHiddenSims(data.visibility || {});
      setFreeSims(data.free || {});
      setEtaSims(data.eta || {});
    } catch {
      /* fall back silently to built-in defaults */
    } finally {
      setContentReady(true);
    }
  }, [user]);

  useEffect(() => {
    if (authReady) refresh();
  }, [authReady, refresh]);

  const isSimVisible = useCallback((id) => hiddenSims[id] !== false, [hiddenSims]);
  const isSimFree = useCallback((id) => freeSims[id] === true, [freeSims]);
  const getSimEta = useCallback((id) => etaSims[id] || null, [etaSims]);

  return (
    <ContentContext.Provider
      value={{ caseOverrides, readingOverrides, hiddenSims, freeSims, etaSims, contentReady, isSimVisible, isSimFree, getSimEta, refresh }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);

// Filter a list of simulator ids to those visible to trainees.
export const useVisibleSims = (ids) => {
  const { isSimVisible } = useContent();
  return ids.filter(isSimVisible);
};

// Return the admin-overridden case list for a simulator, else the built-in default.
export const useCaseList = (key, fallback) => {
  const { caseOverrides } = useContent();
  const o = caseOverrides[key];
  return Array.isArray(o) && o.length ? o : fallback;
};

// Return the admin-overridden further-reading block for a topic, else the default.
export const useReading = (topic, fallback) => {
  const { readingOverrides } = useContent();
  const o = readingOverrides[topic];
  return o && typeof o === "object" ? o : fallback;
};
